import {useMemo} from "react";
import {hooks} from "@advthreat/ribbon-components";
import {FieldNames} from "models/fields";

const {useDispositions} = hooks;

/**
 * Takes the input Observable(s) and attempts to attach disposition data to
 * merge disposition data into the observable object(s).
 *
 * Deliberately made this a more general solution to accept a single Object
 * or an Array:
 * - The `useDisposition` hook which this is based on ultimately accepts
 *   an array so even a single object must be converted into an array
 *   containing that object.
 * - Since we have use cases where we may need to resolve a single
 *   Observable or an array of Observables, this Hook will return the
 *   same type as the input (Object or array).
 * - This abstracts any logic to convert to/from an array away from
 *   the UI component implementing it.
 * - A single hook which can handle both, avoids two hooks which are
 *   almost identical except for how the return the resolved Observable(s)/
 *
 * @param {Object | Array<Object>} data A single observable object or an
 * array of Observable Objects
 * @returns Returns the input data with dispositions data attached to
 * the Observable(s)
 */
export const useDeliberateObservables = (data) => {
  const isMultiple = Array.isArray(data);

  const deliberatedArr = useDispositions(
    useMemo(() => (data ? (isMultiple ? data : [data]) : []), [data]),
    true,
    false,
    {waitForAll: false}
  );

  const displayObservable = useMemo(() => {
    if (!deliberatedArr.length) return data;

    if (isMultiple) {
      return data.map(
        (ogObservable) =>
          ogObservable.disposition
            ? ogObservable // Already has disposition.
            : {
                ...ogObservable,
                ...deliberatedArr.find(findDeliberated(ogObservable))
              } // Merge matching Deliberated Observable with original.
      );
    } else {
      return deliberatedArr.find(findDeliberated(data)) || data;
    }
  }, [deliberatedArr]);

  return isMultiple
    ? displayObservable.length
      ? displayObservable
      : data
    : displayObservable;
};

const findDeliberated = (observable) => (deliberated) =>
  observable[FieldNames.type] === deliberated[FieldNames.type] &&
  observable[FieldNames.value] === deliberated[FieldNames.value];
